<template>
  <BaseWidget
    class="data-table"
    :title="content.title"
    :widgetId="content.id"
    :edit="edit"
    :content="content"
    :isWidgetLoading="isWidgetLoading"
    :getWidgetReport="getWidgetReport"
  >
    <template v-slot:widget-control>
      <div class="widget-controls">
        <v-select
          class="select"
          :items="tableRows"
          :value="rowsPerPageSettings"
          height="48"
          hide-details
          outlined
          label="Rows"
          item-text="title"
          item-value="value"
          @change="onUpdateItemRows"
          elevation="0"
        ></v-select>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              height="48"
              @click="showColumnDialog = !showColumnDialog"
              class="elevation-0 show-columns-btn action-icon"
              v-bind="attrs"
              v-on="on"
            >
              <template v-slot:default>
                <span class="custom-loader">
                  <v-icon light>mdi-format-columns</v-icon>
                </span>
              </template>
            </v-btn>
          </template>
          <span>{{ Lang.showColumns }}</span>
        </v-tooltip>
      </div>
    </template>
    <BaseTable
      :data="data"
      :headers="headers"
      :headerLabels.sync="headerLabels"
      :rowsPerPage="rowsPerPage"
      :isWidgetLoading="isWidgetLoading"
      :content="content"
      :summary-row="summaryRow"
      :footer-props="footerProps"
      @onColumnsChange="onColumnsChange"
      @onColumnsOrderChange="onColumnsOrderChange"
      @onUpdate="onUpdate"
      @onSortChange="onSortChange"
      isTableWidget
    />
    <ColumnsPopup v-model="showColumnDialog" @onColumnsChange="onColumnsChange" :selected-header="headerLabels" />
  </BaseWidget>
</template>

<script lang="ts">
import { omit, pick } from "lodash";
import Vue from "vue";
import { DataOptions } from "vuetify";
import { ThemeOptions } from "vuetify/types/services/theme";
import { mapGetters } from "vuex";

import ColumnsPopup from "@/components/popups/ColumnsPopup.vue";
import BaseTable from "@/components/tables/BaseTable.vue";
import BaseWidget from "@/components/widgets/explore/BaseWidget.vue";
import { METRIC_CHANNEL_OBJ, METRIC_LIFETIME_FRQ, METRIC_LIFETIME_RCH } from "@/constants";
import csvExport, { buildDataTableRows, buildFileName, buildFilterRows, buildHeaderRows } from "@/helpers/csvExport";
import { applyTableFilters, excludeNullFieldUnderConditions } from "@/helpers/filtering";
import Lang from "@/lang";
import { widgetApi } from "@/router";
import inputsDictionary from "@/settings/inputsDictionary";
import { metricsDictionary } from "@/settings/metricsDictionary";
import { defaultTableHeaders, tableRows } from "@/settings/tableSettings";
import { FilterParams } from "@/store/modules/userSettings";
import { TableDataRow } from "@/types/charts";
import { ContentWidget } from "@/types/layouts";
export type LabelType = {
  [key: string]: string;
};
export type TableHeader = {
  title: string;
  value: string;
};
export default Vue.extend({
  name: "TableWidget",

  props: {
    content: Object as () => ContentWidget,
    edit: Boolean,
  },

  components: { ColumnsPopup, BaseWidget, BaseTable },

  watch: {
    filterParams: {
      deep: true,
      async handler(): Promise<void> {
        this.isWidgetLoading = true;
        await this.onUpdate();
        this.isWidgetLoading = false;
      },
    },
  },

  async beforeMount() {
    await this.getSummaryData(this.filterParams);
    this.theme = this.$vuetify.theme;
    this.rowsPerPage = this.rowsPerPageSettings;
    if (this.content?.settings?.["columns"]) {
      this.headerLabels = this.content.settings.columns as TableHeader[];
    } else {
      this.$emit("onColumnsChange", { columns: this.headerLabels, id: this.content.id, isDefault: true });
    }

    await this.onUpdate();
  },

  data() {
    return {
      Lang,
      tableRows,
      options: {} as DataOptions,
      isWidgetLoading: false,
      data: [] as TableDataRow[],
      headerLabels: [...defaultTableHeaders],
      showColumnDialog: false,
      theme: {} as ThemeOptions,
      displayMetrics: inputsDictionary.displayMetrics,
      summaryRow: {} as Record<string, unknown>,
      rowsPerPage: null as null | number,
      footerProps: {
        showCurrentPage: true,
        disableItemsPerPage: true,
        disablePagination: false,
      },
    };
  },
  computed: {
    ...mapGetters("userSettings", ["filterParams", "userSettings", "dashboards"]),

    headers(): LabelType[] {
      const headersWithLabels = [] as LabelType[];
      this.headerLabels.forEach((header: TableHeader, index: number) => {
        if (Number(index) === 0) {
          headersWithLabels.push({
            text: header.title,
            value: header.value,
            width: "150px",
          });
        } else {
          headersWithLabels.push({
            text: header.title,
            value: header.value,
            width: "150px",
          });
        }
      });
      return headersWithLabels;
    },

    widgetId(): string {
      return this.content?.id.replace(" ", "");
    },

    selectedMetrics(): string[] {
      return this.headers.map((item: LabelType) => {
        return item.value as string;
      });
    },

    rowsPerPageSettings(): number {
      return this.content?.settings?.rowsPerPage as number;
    },
  },

  methods: {
    async getData(filter: FilterParams): Promise<void> {
      this.isWidgetLoading = true;
      const result = await widgetApi.getTableData(filter, this.selectedMetrics);
      if (result) {
        // Custom logic added to support a specific ArmCandy requirement — see AC-33
        this.data = applyTableFilters(result.data as TableDataRow[], this.selectedMetrics, [
          excludeNullFieldUnderConditions({
            targetField: METRIC_CHANNEL_OBJ,
            mustInclude: [METRIC_CHANNEL_OBJ],
            mustNotInclude: [METRIC_LIFETIME_RCH, METRIC_LIFETIME_FRQ],
          }),
        ]);
      }
      this.isWidgetLoading = false;
    },

    async getSummaryData(filter: FilterParams): Promise<void> {
      this.isWidgetLoading = true;
      const summaryMetrics = this.selectedMetrics.filter((metricName: string) => {
        return inputsDictionary.displayMetrics.find((item) => {
          if (item.value === metricName) {
            return item.value;
          }
        });
      });
      const result = await widgetApi.getTableData(filter, summaryMetrics);
      if (result?.data) {
        this.summaryRow = { ...result.data[0] };
      }
      this.isWidgetLoading = false;
    },

    onColumnsChange(val: TableHeader[]): void {
      this.headerLabels = val;
      this.$emit("onColumnsChange", { columns: val, id: this.content.id });
      this.onUpdate();
    },

    onColumnsOrderChange(val: Record<string, number>): void {
      const headerSelected = this.headerLabels.splice(Number(val.oldIndex), 1)[0];
      this.headerLabels.splice(Number(val.newIndex), 0, headerSelected);
      this.$emit("onColumnsChange", { columns: this.headerLabels, id: this.widgetId });
    },

    onSortChange(val: Record<string, number>): void {
      this.$emit("onSortChange", val);
    },

    async onUpdate(): Promise<void> {
      await this.getData(this.filterParams);
      await this.getSummaryData(this.filterParams);
    },

    onUpdateItemRows(val: number): void {
      this.rowsPerPage = val;
      this.options.page = 1;
      this.options.itemsPerPage = val;
    },

    getWidgetReport(isSingleChart = true): void {
      let body = widgetApi.getTableRequestBody(this.filterParams, this.selectedMetrics);
      body = omit(body, ["dimension"]);

      const csvContent = [];
      csvContent.push(...buildHeaderRows(this.content?.title));
      csvContent.push(...buildFilterRows(body, isSingleChart));

      csvContent.push([]);
      const dataKeys = this.headers.map((item) => item.value);
      const preparedData = this.data.map((item) => pick(item, dataKeys));
      csvContent.push(dataKeys.map((key) => metricsDictionary.find((item) => item.value === key)?.title || key));

      csvContent.push(...buildDataTableRows(preparedData, dataKeys));

      const fileName = buildFileName(this.content?.title);

      csvExport(csvContent, fileName);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme.scss";
@import "@/scss/custom_variables.scss";
.data-table {
  .widget-controls {
    display: flex;
    align-items: center;
  }
  .select {
    margin-right: 10px !important;
    border-radius: 4px;
  }
}
</style>

<style lang="scss">
@import "@/scss/theme.scss";
.v-select__selection--comma {
  font-size: 16px !important;
  font-weight: 500;
}
.show-columns-btn {
  border: 1px solid rgba(255, 255, 255, 0.24) !important;
  font-size: 16px !important;
  margin-left: 12px;
}
.show-columns-btn:hover {
  border: 1px solid rgba(255, 255, 255, 1) !important;
}
.v-application.theme--dark,
.pdf-container.theme--dark {
  .data-table {
    .select.v-input__control {
      border-radius: 4px !important;
    }
    .v-select__selection--comma {
      color: var(--v-content-white-base);
    }
    .v-text-field--filled > .v-input__control > .v-input__slot,
    .v-text-field--full-width > .v-input__control > .v-input__slot,
    .v-text-field--outlined > .v-input__control > .v-input__slot {
      background-color: var(--v-background-medium-base);
    }
    .show-columns-btn {
      background-color: var(--v-medium-base);
    }
  }
}
.v-application.theme--light,
.pdf-container.theme--light {
  .data-table {
    .v-text-field--filled > .v-input__control > .v-input__slot,
    .v-text-field--full-width > .v-input__control > .v-input__slot,
    .v-text-field--outlined > .v-input__control > .v-input__slot {
      background-color: var(--v-content-light-base);
    }
    .show-columns-btn {
      background-color: var(--v-background-medium-base);
    }
  }
}
</style>
